import { Drawer, Input, Tooltip, Dropdown, Menu } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { listSummaries, regenerateSummary } from '../../store/actions/summary';
import { Button } from '../baseComponents';
import TemplateSelectDropdown from '../Settings/Settings.TemplateDropdown';
import { AmbientloadIcon, ExpandIcon, InfoIcon, MagicIcon } from 'src/icons';
import { analytics } from 'src/services';
import { sanitizeSvg } from 'src/utils';
import { ButtonGroup } from '../baseComponents/ButtonGroup';
import {
  useAuth,
  useFeatureEntitlements,
  usePermissionEntitlements,
} from '@frontegg/react';
import {
  DEFAULT_FORMAT,
  DEFAULT_VERBOSITY,
  formatOptions,
  verbosityOptions,
} from '../../utils/encounterConstant';
import { isAllowedToGenerateSummary } from '../PastEncounter/permissions';
import { SupaDemo } from 'src/components/groupComponents/supaDemo';
import { useResolvedVerbosity } from 'src/hooks/encounter/useResolvedEncounterConfig';

const { TextArea } = Input;

export const EncounterSummaryMagicEdit = ({
  isMagicEditingRef,
  encounterDetails,
  encounterPhase,
  searchFilters,
  encounter_id,
  schedulepage,
  restrictTemplates,
  suggestions,
}) => {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const { isEntitled: hasGlobalNoteFormatEnabled } = useFeatureEntitlements(
    'global-note-format-preference'
  );

  const { isEntitled: hasAutoSuggestionsAccess } =
    useFeatureEntitlements('auto-suggestions');

  const { isEntitled: canGenerateInVisitSummary } = usePermissionEntitlements(
    'encounter.in-visit.generate'
  );
  const { isEntitled: canGeneratePreChartSummary } = usePermissionEntitlements(
    'encounter.pre-chart.generate'
  );
  const { isEntitled: canGenerateAfterVisitSummary } =
    usePermissionEntitlements('encounter.after-visit.generate');

  const { isSummaryRegenerationLoading } = useSelector(
    (state) => state?.summaryState
  );
  const { currentPractitioner, currentPractitionerSettings } = useSelector(
    (state) => state?.practitionerState
  );

  // Keep drawer open if regeneration API is stil in-flight.
  const [drawerVisible, setDrawerVisible] = useState(
    isSummaryRegenerationLoading
  );

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  const [customInstructions, setCustomInstructions] = useState('');
  const [workingTemplate, setWorkingTemplate] = useState(null);
  const isSmallScreen = window.innerWidth <= 1536;
  const isMobileView = window.innerWidth <= 1260;

  const { verbosity: resolvedPractitionerVerbosity } = useResolvedVerbosity({
    practitioner_id: encounterDetails?.practitioner_id,
    encounter_verbosity: encounterDetails?.summary_verbosity,
    currentPractitionerSettings,
    currentPractitionerId: currentPractitioner?.practitioner_id,
  });

  const [selectedSummaryVerbosity, setSummaryVerbosity] = useState();
  const [selectedSummaryFormat, setSummaryFormat] = useState();
  const summaryVerbosity = useMemo(() => {
    if (selectedSummaryVerbosity) {
      return selectedSummaryVerbosity;
    }

    if (resolvedPractitionerVerbosity) {
      return resolvedPractitionerVerbosity;
    }

    if (workingTemplate?.body?.verbosity) {
      return workingTemplate.body.verbosity;
    }

    return DEFAULT_VERBOSITY;
  }, [
    selectedSummaryVerbosity,
    resolvedPractitionerVerbosity,
    workingTemplate,
  ]);

  const summaryFormat = useMemo(() => {
    if (selectedSummaryFormat) {
      return selectedSummaryFormat;
    }

    if (encounterDetails?.summary_format) {
      return encounterDetails.summary_format;
    }

    if (workingTemplate?.body?.format) {
      return workingTemplate.body.format;
    }

    return DEFAULT_FORMAT;
  }, [selectedSummaryFormat, encounterDetails, workingTemplate]);

  const lastEncounterId = useRef(encounterDetails?.encounter_id);

  useEffect(() => {
    if (lastEncounterId.current !== encounterDetails?.encounter_id) {
      setSummaryVerbosity(undefined);
      setSummaryFormat(undefined);
      setCustomInstructions('');
      lastEncounterId.current = encounterDetails?.encounter_id;
    }
  }, [encounterDetails]);

  const onClick = () => {
    setDrawerVisible(true);
    isMagicEditingRef.current = true;
    if (schedulepage) {
      analytics.track(
        'Clicked Magic Edit In Ambient Medical Conversation From VCA',
        {
          encounter: encounterDetails?.encounter_id,
          email: user?.email,
        }
      );
    } else {
      analytics.track(
        'Clicked Magic Edit In Ambient Medical Conversation From Aura',
        {
          encounter: encounterDetails?.encounter_id,
          email: user?.email,
        }
      );
    }
  };

  const stringToColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    const colors = [
      '#6366f1',
      '#ec4899',
      '#10b981',
      '#f59e0b',
      '#ef4444',
      '#8b5cf6',
      '#06b6d4',
      '#4a5565',
    ];

    return colors[Math.abs(hash) % colors.length];
  };

  const onClose = () => {
    if (!isSummaryRegenerationLoading) {
      isMagicEditingRef.current = false;
      setDrawerVisible(false);
    }
  };

  const updateSummariesAfterMagicEdit = (encounterId) => {
    setTimeout(async () => {
      try {
        if (lastEncounterId.current !== encounterId || !isMounted.current) {
          return; // Skip refresh if the user switched encounters
        }
        dispatch(listSummaries(encounter_id, encounterPhase, 'ambient'));
        dispatch(listSummaries(encounter_id, encounterPhase, 'icd-10'));
        dispatch(listSummaries(encounter_id, encounterPhase, 'dictate'));
        dispatch(listSummaries(encounter_id, encounterPhase, 'cpt'));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }, 30000); // Refreshing after 30 seconds delay so that new summaries has been regenerated
  };
  const onSubmit = (mode = 'update') => {
    const currentEncounterId = encounterDetails?.encounter_id;
    dispatch(
      regenerateSummary(
        encounterDetails?.encounter_id,
        encounterPhase,
        searchFilters,
        customInstructions,
        workingTemplate,
        summaryVerbosity,
        summaryFormat,
        mode
      )
    ).then((success) => {
      if (success) {
        setDrawerVisible(false);
        updateSummariesAfterMagicEdit(currentEncounterId);
        isMagicEditingRef.current = false;
      }
    });
    if (schedulepage) {
      analytics.track(
        'Clicked Regenerate Medical Conversation In Ambient From VCA',
        {
          encounter: encounterDetails?.encounter_id,
          email: user?.email,
        }
      );
    } else {
      analytics.track(
        'Clicked Regenerate Medical Conversation In Ambient From Aura',
        {
          encounter: encounterDetails?.encounter_id,
          email: user?.email,
        }
      );
    }
  };

  const menu = (
    <Menu>
      <Menu.Item key="1" onClick={() => onSubmit('overwrite')}>
        Regenerate from scratch
      </Menu.Item>
    </Menu>
  );

  let templateId;
  let templateOverridesId;

  if (encounterPhase == 'in-visit') {
    templateId = encounterDetails.in_visit_template_id;
    templateOverridesId = encounterDetails.in_visit_template_overrides_id;
  } else if (encounterPhase == 'pre-chart') {
    templateId = encounterDetails.pre_chart_template_id;
    templateOverridesId = encounterDetails.pre_chart_template_overrides_id;
  } else if (encounterPhase == 'after-visit') {
    templateId = encounterDetails.after_visit_template_id;
    templateOverridesId = encounterDetails.after_visit_template_overrides_id;
  }

  const showWarning = useMemo(() => {
    const templateChanged =
      (workingTemplate?.template_id ?? null) !== (templateId ?? null) ||
      (workingTemplate?.template_overrides_id ?? null) !==
        (templateOverridesId ?? null);

    const verbosityChanged =
      (encounterDetails?.summary_verbosity ?? null) !== null &&
      (encounterDetails?.summary_verbosity ?? null) !==
        (summaryVerbosity ?? null);

    return templateChanged || verbosityChanged;
  }, [
    workingTemplate,
    templateId,
    templateOverridesId,
    encounterDetails?.summary_verbosity,
    summaryVerbosity,
  ]);

  const isAllowedToMagicEdit = useMemo(() => {
    return isAllowedToGenerateSummary({
      encounterPhase,
      canGenerateInVisitSummary,
      canGeneratePreChartSummary,
      canGenerateAfterVisitSummary,
    });
  }, [
    encounterPhase,
    canGenerateInVisitSummary,
    canGeneratePreChartSummary,
    canGenerateAfterVisitSummary,
  ]);

  return (
    <>
      <Tooltip title="Magic edit" placement="bottom">
        <div>
          <Button
            onClick={onClick}
            type={'button'}
            className="h-[35px] cursor-pointer"
            id="magic icon"
            disabled={!isAllowedToMagicEdit}
          >
            {isSmallScreen ? (
              <div className="flex items-center py-[1px] text-xs gap-2">
                <MagicIcon />
              </div>
            ) : isMobileView ? (
              <div className="flex items-center text-xs gap-1">
                <MagicIcon />
              </div>
            ) : (
              <div className="flex items-center py-[1px] text-xs gap-2">
                <MagicIcon />
                Magic edit
              </div>
            )}
          </Button>
        </div>
      </Tooltip>

      <Drawer
        title="Magic edit"
        placement="right"
        closable={true}
        onClose={onClose}
        open={drawerVisible}
        width={720}
        style={{ fontFamily: 'Poppins' }}
      >
        <div className="mb-4 py-3 px-1 flex items-center bg-[#e6f4ff] rounded-lg border border-[#91caff] pr-2 mt-1">
          <div className="mt-2 ml-2">
            <InfoIcon />
          </div>
          <div className="text-[#000] mt-0.5">
            Magic Edits are temporary and only affect this note. They are not
            carried forward to future notes. To apply these instructions
            permanently across all notes, create a custom template.
            <a
              href="#"
              className=" cursor-pointer ml-1 text-[#5a8dda] underline"
            >
              <SupaDemo>
                <span
                  data-supademo-demo="clz07qg9i2ibdz9kd5sj5nz70"
                  onClick={() => {
                    analytics.track(
                      'Clicked Custom Template Guided Tour From Magic Edit ',
                      {
                        email: user?.email,
                      }
                    );
                  }}
                >
                  Learn more
                </span>
              </SupaDemo>
            </a>
          </div>
        </div>
        {showWarning && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-800">
            ⚠️ Detected a change to either the template or verbosity. Any manual
            / dictation edits made since the last generation will be lost.
          </div>
        )}
        <div className="text-[#667085] mb-1">Note template</div>
        <TemplateSelectDropdown
          allowInlineEdit
          encounterPhase={encounterPhase}
          currentTemplate={workingTemplate}
          setCurrentTemplate={setWorkingTemplate}
          templateId={templateId}
          templateOverridesId={templateOverridesId}
          fromIsSettings={false}
          restrictTemplates={restrictTemplates}
        />

        {encounterPhase === 'in-visit' && (
          <>
            <div className="mt-4">
              <div className="text-[#667085] mb-1">Note verbosity</div>
              <ButtonGroup
                options={verbosityOptions}
                value={summaryVerbosity}
                onChange={(value) => {
                  analytics.track('Clicked Verbosity Option In Magic Edit', {
                    verbosity: value,
                    email: user?.email,
                  });
                  setSummaryVerbosity(value);
                }}
              />
            </div>
            {!hasGlobalNoteFormatEnabled && (
              <div className="mt-4">
                <div className="text-[#667085] mb-1">Note format</div>
                <ButtonGroup
                  options={formatOptions}
                  value={summaryFormat}
                  onChange={(value) => {
                    analytics.track('Clicked Format Option In Magic Edit', {
                      format: value,
                      email: user?.email,
                    });
                    setSummaryFormat(value);
                  }}
                />
              </div>
            )}
          </>
        )}

        <div className="mt-4">
          <div className="text-[#667085] mb-1">Additional instructions</div>
          <div className="grid grid-cols-1 gap-4 mt-2 border border-[#d9d9d9] rounded-md">
            <TextArea
              bordered={false}
              rows={5}
              placeholder="Add additional instructions to be used to generate a new summary."
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              style={{ resize: 'none', fontFamily: 'Poppins' }}
            />
          </div>
        </div>
        {hasAutoSuggestionsAccess && (
          <div className="flex flex-wrap gap-2 mt-2">
            {suggestions?.[encounterPhase]?.map((item, index) => {
              const color = stringToColor(item.suggestion);
              const handleSuggestionClick = () => {
                setCustomInstructions(`${item.suggestion} `);
              };
              return (
                <div
                  key={index}
                  className="flex items-center px-3 py-1 gap-1 mt-2 w-fit rounded-3xl cursor-pointer transition-colors duration-200"
                  style={{
                    border: `1.2px solid ${color}`,
                    backgroundColor: 'transparent',
                    ':hover': {
                      backgroundColor: `${color}20`, // Adding 20 for 12.5% opacity
                    },
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${color}20`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  onClick={handleSuggestionClick}
                >
                  <div className="flex items-center gap-1">
                    <span
                      className="w-4 h-4"
                      style={{ fill: color }}
                      dangerouslySetInnerHTML={{
                        __html: sanitizeSvg(item.icon),
                      }}
                    />
                    <Tooltip
                      title={item.suggestion}
                      placement="topLeft"
                      getPopupContainer={() => document.body}
                      styles={{
                        body: {
                          maxWidth: '300px',
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                          overflow: 'hidden',
                        },
                      }}
                    >
                      <span className="text-sm truncate max-w-[170px]">
                        {item.suggestion}
                      </span>
                    </Tooltip>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex justify-start mt-6">
          <Dropdown.Button
            type="primary"
            onClick={() => onSubmit('update')}
            placement="bottomLeft"
            overlay={menu}
            rootClassName="w-auto min-w-[120px] copy-all rounded-xl drop-shadow-sm flex bg-[#00D090] hover:bg-[#059669] text-white [&_.ant-btn-loading-icon]:text-white"
            loading={isSummaryRegenerationLoading}
            icon={<ExpandIcon fill={'#ffffff'} />}
          >
            <div className="flex items-center justify-center cursor-pointer gap-2 text-white w-full">
              {!isSummaryRegenerationLoading && (
                <>
                  <AmbientloadIcon />
                  <span>Regenerate</span>
                </>
              )}
            </div>
          </Dropdown.Button>
        </div>
      </Drawer>
    </>
  );
};
